import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { MenuItem } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { BaseMerchantNewsArticle, MerchantArticleStatus } from "../merchantNewsItemType"
import {
    FetchMerchantAdminNewsResponse,
    MerchantAdminNewsQuery,
    deleteMerchantAdminNews,
    fetchMerchantAdminArchiveNews,
} from "../merchantAdminNewsApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { getEditNewsRoute } from "../../../../../constants/routes"
import { Badge } from "../components/Badge"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { DestructiveMenuItem } from "../../../../../shared/components/menus/DestructiveMenuItem"

const selectNews = (data: FetchMerchantAdminNewsResponse) => data?.news || []

export const MerchantArchiveNewsTab = () => {
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>({
        showConfirmModal: false,
        confirmModalText: "",
        itemId: null,
    })

    const { merchantId } = useMerchant()

    const queryResult = useApiQuery(fetchMerchantAdminArchiveNews, {
        queryName: MerchantAdminNewsQuery.ARCHIVE_LIST,
        dependencies: {
            merchantId,
        },
        select: selectNews,
    })

    const { mutateAsync: callDeleteMerchantAdminNews } = useApiMutation(deleteMerchantAdminNews, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantAdminNewsQuery.ARCHIVE_LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data?.serviceResponse?.displayMessage ?? l10n.getString("news-table-delete-success-message"),
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteMerchantAdminNews({ merchantId, articleId: confirmModalState.itemId })
        } else {
            throw new Error("News id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteMerchantAdminNews])

    const { ApiQueryTable, columnFactory } = useApiQueryTable(queryResult)

    const columns = useMemo<MRT_ColumnDef<BaseMerchantNewsArticle>[]>(
        () => [
            columnFactory.nameColumn({ header: l10n.getString("news-archive-title"), accessorKey: "subject" }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("news-archive-expiration-date"),
                    accessorKey: "expirationDate",
                },
                (row) => row.expirationDate,
            ),
            columnFactory.statusColumn({
                header: l10n.getString("news-archive-active-status"),
                accessorKey: "status",
                Cell: ({ row }) => {
                    const value = row.original.status

                    return <>{Badge({ articleStatus: value ?? MerchantArticleStatus.EXPIRED, className: "" })}</>
                },
            }),
        ],
        [columnFactory, l10n],
    )

    return (
        <>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="news-edit">
                        <MenuItem
                            key={row.original.id}
                            onClick={() => {
                                navigate(getEditNewsRoute(row.original.id))
                                closeMenu()
                            }}
                        >
                            Edit
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="news-delete">
                        <DestructiveMenuItem
                            key={row.original.id}
                            onClick={() => {
                                setConfirmModalState({
                                    showConfirmModal: true,
                                    confirmModalText: `${row.original.subject}`,
                                    itemId: row.original.id,
                                })
                                closeMenu()
                            }}
                        >
                            Delete
                        </DestructiveMenuItem>
                    </LocalizedStrict>,
                ]}
            />

            {confirmModalState.showConfirmModal && (
                <DeleteConfirmDialog
                    open={confirmModalState.showConfirmModal}
                    typeOfEntity={l10n.getString("create-or-edit-merchant-article-delete")}
                    entityName={confirmModalState.confirmModalText}
                    handleClick={handleDeleteClick}
                    onClose={() =>
                        setConfirmModalState({
                            showConfirmModal: false,
                            confirmModalText: "",
                            itemId: null,
                        })
                    }
                />
            )}
        </>
    )
}
